import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { Check, Film, Image as ImageIcon, ListMusic, PictureInPicture2 } from 'lucide-react'
import VideoPlayerCore from 'components/answers/VideoPlayerCore'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import TvMinimalPlayIcon from 'components/shared/icons/TvMinimalPlayIcon'
import {
    openTrackInCinema,
    playTrackInDock,
    toggleTrackInPlaylist,
} from 'components/shared/MediaDock/mediaDockEvents'
import { PLAY_IN_DOCK_LABEL } from 'components/shared/MediaDock/mediaDockPlayInDock'
import {
    playlistMenuLabel,
    useMediaKeyInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import { buildMediaGridVideoKey } from './mediaGridTrack'
import styles from './media-grid.module.scss'
import { mediaDockCinemaEnabled } from 'lib/mediaDockCinema'
import {
    mediaBucketsEnabled,
    bucketedMediaHeight,
    bucketedMediaGridLayout,
    RESERVED_URL_CARD_IMAGE_HEIGHT,
} from 'lib/mediaBuckets'

const MEDIA_GRID_GAP_PX = 2

function numericDimension(value) {
    const dimension = Number(value)
    return Number.isFinite(dimension) && dimension > 0 ? Math.round(dimension) : null
}

function mediaAspectRatio(item) {
    const width = numericDimension(item?.width)
    const height = numericDimension(item?.height)
    if (width && height) return `${width} / ${height}`
    return '16 / 9'
}

// Fixed-height frame for a single media item under the flag: bucket by aspect from the item's dims
// (same as images elsewhere) so the estimator can reserve the identical height, else a flat frame.
function singleItemBucketHeight(item) {
    const width = numericDimension(item?.width)
    const height = numericDimension(item?.height)
    return width && height ? bucketedMediaHeight(width, height) : RESERVED_URL_CARD_IMAGE_HEIGHT
}

function bucketLayoutForItems(items) {
    const layout = bucketedMediaGridLayout(items.map(singleItemBucketHeight), MEDIA_GRID_GAP_PX)
    if (!layout) return null

    return {
        height: layout.height,
        gridTemplateRows:
            layout.rowHeight == null
                ? `${layout.height}px`
                : `${layout.rowHeight}px ${layout.rowHeight}px`,
    }
}

/**
 * @typedef {Object} MediaItem
 * @property {'image'|'video'|'pending'} type - 'pending' reserves the tile while media syncs
 * @property {string} url - Image src or video src
 * @property {string} [thumbnailUrl] - Video thumbnail (required for videos)
 * @property {number} [width] - Media intrinsic width when available
 * @property {number} [height] - Media intrinsic height when available
 */

function MediaGridVideo({ videoUrl, thumbnailUrl, width, height, answerId, onMediaError }) {
    const mediaKey = buildMediaGridVideoKey(videoUrl, answerId)

    return (
        <VideoPlayerCore
            mediaKey={mediaKey}
            src={videoUrl}
            type="video/mp4"
            trackAnswerId={answerId}
            posterSrc={thumbnailUrl}
            width={width}
            height={height}
            className={styles.videoCore}
            containerClassName={styles.videoCoreContainer}
            posterClassName={styles.videoPoster}
            videoClassName={styles.video}
            preloadVideoLink
            preloadVideoOnHover
            keepChromeVisibleOnHover
            loadWithoutPoster={!thumbnailUrl}
            fallbackAspectRatio="16 / 9"
            onPosterError={() => onMediaError?.()}
            onVideoError={() => onMediaError?.()}
        />
    )
}

function MediaGridActionsMenu({ videoUrl, answerId, matchingBsvAttachment = null }) {
    const [actionsMenuOpen, setActionsMenuOpen] = useState(false)
    const actionsMenuRef = useRef(null)
    const mediaKey = buildMediaGridVideoKey(videoUrl, answerId)
    const inPlaylist = useMediaKeyInPlaylist(mediaKey)
    const bsvTx = matchingBsvAttachment?.bsv_tx
    const txId = bsvTx?.tx_id || null
    const ActionsIcon = mediaKey ? Film : ImageIcon

    useEffect(() => {
        if (!actionsMenuOpen) return undefined

        const handleOutsidePointerDown = event => {
            if (!actionsMenuRef.current?.contains(event.target)) {
                setActionsMenuOpen(false)
            }
        }

        document.addEventListener('pointerdown', handleOutsidePointerDown)
        return () => document.removeEventListener('pointerdown', handleOutsidePointerDown)
    }, [actionsMenuOpen])

    const handleTogglePlaylist = event => {
        event.preventDefault()
        event.stopPropagation()
        if (!mediaKey) return
        toggleTrackInPlaylist(mediaKey, 'twitter_video_button')
        setActionsMenuOpen(false)
    }

    const handlePlayInDock = event => {
        event.preventDefault()
        event.stopPropagation()
        if (!mediaKey) return
        playTrackInDock(mediaKey, 'twitter_media_menu')
        setActionsMenuOpen(false)
    }

    const handleOpenCinema = event => {
        event.preventDefault()
        event.stopPropagation()
        if (!mediaKey) return

        const menuEl = actionsMenuRef.current
        const originEl = Array.from(
            menuEl?.parentElement?.querySelectorAll('[data-media-track-origin]') || [],
        ).find(element => element.getAttribute('data-media-track-origin') === mediaKey)

        openTrackInCinema(mediaKey, 'twitter_media_menu', {
            originEl: originEl || null,
            returnFocusEl: menuEl?.querySelector('button[aria-haspopup="menu"]') || null,
        })
        setActionsMenuOpen(false)
    }

    const handleOnchainClick = event => {
        event.preventDefault()
        event.stopPropagation()
        setActionsMenuOpen(false)
        if (txId) {
            window.open(bsvTxExplorerUrl(txId), '_blank', 'noopener,noreferrer')
        }
    }

    if (!mediaKey && !matchingBsvAttachment) return null

    return (
        <div className={styles.videoActionsMenu} ref={actionsMenuRef}>
            <button
                type="button"
                className={styles.videoActionsMenuButton}
                aria-label={mediaKey ? 'Twitter video actions' : 'Twitter media actions'}
                aria-haspopup="menu"
                aria-expanded={actionsMenuOpen}
                title={mediaKey ? 'Twitter video actions' : 'Twitter media actions'}
                onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    setActionsMenuOpen(open => !open)
                }}
            >
                <ActionsIcon size={16} strokeWidth={2.25} aria-hidden="true" />
            </button>
            {actionsMenuOpen && (
                <div className={styles.videoActionsMenuList} role="menu">
                    {matchingBsvAttachment && (
                        <button
                            type="button"
                            className={styles.videoActionsMenuItem}
                            role="menuitem"
                            onClick={handleOnchainClick}
                        >
                            <BsvTransactionStatusIcon
                                bsvTx={bsvTx}
                                fallbackTxId={txId}
                                className={styles.videoActionsMenuStatusIcon}
                                disableLink
                            />
                            <span>Onchain</span>
                        </button>
                    )}
                    {mediaKey && mediaDockCinemaEnabled() && (
                        <button
                            type="button"
                            className={styles.videoActionsMenuItem}
                            role="menuitem"
                            onClick={handleOpenCinema}
                        >
                            <TvMinimalPlayIcon size={14} strokeWidth={2.2} aria-hidden="true" />
                            <span>Cinema</span>
                        </button>
                    )}
                    {mediaKey && (
                        <button
                            type="button"
                            className={styles.videoActionsMenuItem}
                            role="menuitem"
                            onClick={handleTogglePlaylist}
                        >
                            {inPlaylist ? (
                                <Check size={14} strokeWidth={2.2} aria-hidden="true" />
                            ) : (
                                <ListMusic size={14} strokeWidth={2.2} aria-hidden="true" />
                            )}
                            <span>{playlistMenuLabel(inPlaylist)}</span>
                        </button>
                    )}
                    {mediaKey && (
                        <button
                            type="button"
                            className={styles.videoActionsMenuItem}
                            role="menuitem"
                            onClick={handlePlayInDock}
                        >
                            <PictureInPicture2 size={14} strokeWidth={2.2} aria-hidden="true" />
                            <span>{PLAY_IN_DOCK_LABEL}</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

/**
 * MediaGrid - Displays 1-4 media items in a Twitter-style grid layout
 *
 * @param {Object} props
 * @param {MediaItem[]} props.items - Array of media items (max 4)
 * @param {(index: number) => void} [props.onImageClick] - Called when image is clicked
 * @param {(item: MediaItem, index: number) => void} [props.onMediaError] - Called when media fails to load
 * @param {boolean} [props.showMediaActionsMenu] - Show the actions menu over the media grid
 * @param {Object} [props.matchingBsvAttachment] - Onchain attachment for this media group
 * @param {string|number} [props.answerId] - Owning Treechat post for dock actions
 */
export default function MediaGrid({
    items,
    onImageClick,
    onMediaError,
    showMediaActionsMenu = false,
    matchingBsvAttachment = null,
    answerId = null,
}) {
    if (!items || items.length === 0) {
        return null
    }

    // Limit to 4 items
    const displayItems = items.slice(0, 4)
    const count = displayItems.length
    const playlistVideo = displayItems.find(item => item.type === 'video')
    const useMediaBuckets = mediaBucketsEnabled()
    const bucketLayout = useMediaBuckets ? bucketLayoutForItems(displayItems) : null

    const handleImageClick = (e, index) => {
        e.preventDefault()
        e.stopPropagation()
        onImageClick?.(index)
    }

    return (
        <div
            className={cn(
                styles.mediaGrid,
                styles[`count${count}`],
                useMediaBuckets && styles.mediaGridBucketed,
            )}
            style={bucketLayout || undefined}
        >
            {displayItems.map((item, index) => (
                <div
                    key={index}
                    className={cn(
                        styles.mediaItem,
                        item.type === 'pending' && styles.pendingMediaItem,
                    )}
                    aria-hidden={item.type === 'pending' ? true : undefined}
                    style={
                        useMediaBuckets
                            ? count === 1
                                ? { height: singleItemBucketHeight(item), width: '100%' }
                                : { height: '100%', width: '100%', aspectRatio: 'auto' }
                            : count === 1
                            ? { aspectRatio: mediaAspectRatio(item) }
                            : undefined
                    }
                >
                    {item.type === 'pending' ? null : item.type === 'image' ? (
                        <img
                            src={item.url}
                            width={numericDimension(item.width) || undefined}
                            height={numericDimension(item.height) || undefined}
                            style={
                                useMediaBuckets
                                    ? {
                                          width: '100%',
                                          height: '100%',
                                          maxHeight: 'none',
                                          objectFit: count === 1 ? 'contain' : 'cover',
                                      }
                                    : undefined
                            }
                            onClick={e => handleImageClick(e, index)}
                            onError={() => onMediaError?.(item, index)}
                            loading="lazy"
                            alt=""
                        />
                    ) : (
                        <MediaGridVideo
                            videoUrl={item.url}
                            thumbnailUrl={item.thumbnailUrl}
                            width={item.width}
                            height={item.height}
                            answerId={answerId}
                            onMediaError={() => onMediaError?.(item, index)}
                        />
                    )}
                </div>
            ))}
            {showMediaActionsMenu && (
                <MediaGridActionsMenu
                    videoUrl={playlistVideo?.url}
                    answerId={answerId}
                    matchingBsvAttachment={matchingBsvAttachment}
                />
            )}
        </div>
    )
}
